import { useState, useEffect, useCallback, useRef } from 'react';

interface LoadingScreenProps {
  isAuthLoading?: boolean;
  onComplete: () => void;
}

const TOTAL_STEPS = 8;

// Simple fetch wrapper that warms up API endpoints
const warmupFetch = async (url: string) => {
  try {
    await fetch(url, { credentials: 'include' });
  } catch {
    // Ignore errors - this is just for warming up
  }
};

export default function LoadingScreen({ isAuthLoading, onComplete }: LoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const hasPreloaded = useRef(false);

  const preloadData = useCallback(async () => {
    if (hasPreloaded.current) return;
    hasPreloaded.current = true;

    const today = new Date().toISOString().split('T')[0];
    
    // Warm up all API endpoints in parallel batches for speed
    const endpoints = [
      '/api/exercises',
      '/api/workouts?limit=20',
      '/api/measurements?limit=10',
      `/api/nutrition/water?date=${today}`,
      `/api/nutrition/meals?date=${today}`,
      '/api/analytics/weekly-live',
      '/api/analytics/records',
      '/api/analytics/calendar',
    ];

    // Animate progress while fetching
    let completed = 0;
    
    const promises = endpoints.map(async (endpoint) => {
      await warmupFetch(endpoint);
      completed++;
      setProgress(Math.round((completed / TOTAL_STEPS) * 100));
    });

    await Promise.all(promises);

    // Small delay before completing
    setTimeout(() => {
      onComplete();
    }, 200);
  }, [onComplete]);

  // Animate progress during auth loading
  useEffect(() => {
    if (isAuthLoading) {
      // Slowly animate from 0 to ~30 during auth check
      const interval = setInterval(() => {
        setProgress((p) => Math.min(p + 2, 30));
      }, 50);
      return () => clearInterval(interval);
    }
  }, [isAuthLoading]);

  // Start data preloading when auth is done
  useEffect(() => {
    if (!isAuthLoading && !hasPreloaded.current) {
      // Reset progress to continue from current position
      setProgress((p) => Math.max(p, 30));
      preloadData();
    }
  }, [isAuthLoading, preloadData]);

  return (
    <div className="fixed inset-0 bg-primary-600 flex items-end justify-end z-[9999]">
      {/* Progress number */}
      <div className="p-8">
        <span className="text-[180px] font-bold text-white/90 leading-none tracking-tighter">
          {progress}
        </span>
      </div>
    </div>
  );
}
